import { memo, useEffect, useRef } from 'react';
import { Hand, Mic, MicOff, MonitorUp, Pin, Star } from 'lucide-react';
import { Avatar, cn } from '@cyphex/ui';
import type { MediaClient, VideoSource } from '../media/contract';
import type { TileView } from '../media/tilePlan';
import { QualityIndicator } from './QualityIndicator';

export interface VideoTileProps {
  tile: TileView;
  client: MediaClient;
  source?: VideoSource;
  /**
   * How the frame meets the tile box. A camera is cropped to fill it
   * (`cover`); a SCREEN share must never be cropped — cropping a shared
   * document or terminal loses the very content being presented — so it is
   * letterboxed at its own aspect ratio (`contain`). Defaults by `source`.
   */
  fit?: 'cover' | 'contain';
  pinned?: boolean;
  onPin?: (participantId: string | null) => void;
  className?: string;
}

/**
 * One video tile. The <video> element is attached ONLY while the tile has an
 * allowed layer — an off-page or suspended tile never holds a track, which is
 * what stops the SFU forwarding it.
 */
export const VideoTile = memo(function VideoTile({
  tile, client, source = 'camera', fit, pinned = false, onPin, className,
}: VideoTileProps) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const { participant, layer } = tile;
  const live = layer !== null && (source === 'screen' || participant.cameraOn);
  const contain = (fit ?? (source === 'screen' ? 'contain' : 'cover')) === 'contain';
  /* Only a camera is mirrored. Mirroring your own SHARED SCREEN would render
     every line of text on it backwards. */
  const mirrored = participant.isLocal && source === 'camera';

  useEffect(() => {
    const el = videoRef.current;
    if (!el || !live) return;
    const detach = client.attachVideo(participant.id, source, el);
    return detach;
  }, [client, participant.id, source, live, layer]);

  return (
    <div
      className={cn(
        'group relative overflow-hidden rounded-md bg-stage-tile shadow-tile',
        /* The speaking ring is INSET, so on a screen share it would paint over
           the outermost pixels of the presented document. A shared surface is
           never ringed — the nameplate already says who is presenting. */
        participant.speaking && source === 'camera' && 'ring-3 ring-inset ring-stage-speaking',
        !live && 'stage-camoff',
        className,
      )}
      data-layer={layer ?? 'off'}
    >
      {live ? (
        <video
          ref={videoRef}
          autoPlay
          playsInline
          muted
          disablePictureInPicture
          className={cn(
            'h-full w-full',
            contain ? 'object-contain' : 'object-cover',
            mirrored && 'scale-x-[-1]',
          )}
        />
      ) : (
        <div className="flex h-full w-full items-center justify-center">
          <Avatar name={participant.name} src={participant.avatarUrl} size="3xl" ring />
        </div>
      )}

      {/*
        ON A SHARE, EVERY PIXEL IS SOMEBODY ELSE'S DOCUMENT.

        This file already refuses to paint a speaking ring over a shared surface
        for exactly this reason (see the ring above) — and then parked an opaque
        nameplate in the bottom-left corner of it regardless, sitting over the
        presenter's dock, taskbar, or whatever else lives down there.

        On a CAMERA tile the nameplate is the only thing identifying a face and
        must stay. On a SHARE it is redundant — but only because something else
        says who is presenting, and for a long time nothing did: the People
        panel had no share glyph, and the presence badge is never written for a
        call at all. Both are answered now, and neither costs the presenter any
        content: `PresenterBar` names the presenter above every layout, and the
        glyph below marks their camera tile in the strip.

        Opacity rather than unmounting, so the name stays in the accessibility
        tree for anyone not reading the strip, and nothing reflows on hover.
      */}
      <div
        className={cn(
          'absolute bottom-2 left-2 flex items-center gap-1.5',
          source === 'screen' &&
            'opacity-0 transition-opacity duration-fast group-hover:opacity-100',
        )}
      >
        <span className="stage-nameplate">
          {participant.name}
          {participant.micOn ? (
            <Mic size={14} strokeWidth={2.5} className="text-success" aria-label="Unmuted" />
          ) : (
            <MicOff size={14} strokeWidth={2.5} className="text-destructive" aria-label="Muted" />
          )}
        </span>
        {/* Only surfaced when it is worth acting on — a green meter on every
            tile is noise, and noise is what people learn to stop reading. */}
        {participant.quality === 'poor' || participant.quality === 'lost' ? (
          <span className="stage-nameplate">
            <QualityIndicator quality={participant.quality} name={participant.name} />
          </span>
        ) : null}
      </div>

      <div className="absolute right-2 top-2 flex items-center gap-1.5">
        {/* On the CAMERA tile only: on the share itself it would be pointing at
            the thing it is standing on, and it would sit over the presenter's
            own screen — which this file refuses to do for the ring and the
            nameplate for exactly the same reason. */}
        {participant.sharing && source === 'camera' ? (
          <span className="stage-nameplate" title="Presenting">
            <MonitorUp size={14} strokeWidth={2.5} className="text-info" aria-label="Presenting" />
          </span>
        ) : null}
        {participant.handRaisedAt !== null ? (
          <span className="stage-nameplate" title="Hand raised">
            <Hand size={14} strokeWidth={2.5} className="text-warning" aria-label="Hand raised" />
          </span>
        ) : null}
        {participant.spotlighted ? (
          <span className="stage-nameplate" title="Spotlighted">
            <Star size={14} strokeWidth={2.5} className="text-warning" aria-label="Spotlighted" />
          </span>
        ) : null}
        {onPin ? (
          <button
            type="button"
            onClick={() => onPin(pinned ? null : participant.id)}
            aria-label={pinned ? `Unpin ${participant.name}` : `Pin ${participant.name}`}
            className={cn(
              'stage-nameplate opacity-0 transition-opacity duration-fast',
              'focus-visible:opacity-100 group-hover:opacity-100',
              pinned && 'opacity-100',
            )}
          >
            <Pin size={14} strokeWidth={2.5} aria-hidden />
          </button>
        ) : null}
      </div>
    </div>
  );
});
